import { api } from "@/lib/api-client";

export interface CandidateForm {
  id: string;
  name: string;
  classificationKey: string;
  /** R-266 6a — owning job type, so the CS picker can span + group by job type
   *  under cross-type routing. Null for legacy/own-type-only candidates. */
  jobTypeId: string | null;
  jobTypeName: string | null;
}

export interface ClassifyFormPageType {
  id: string;
  name: string;
  ordinal: number;
}

export interface ClassifyForm {
  allowsAttachments: boolean;
  pageTypes: ClassifyFormPageType[];
}

export interface ClassifyItem {
  id: string;
  displayId: string | null;
  filename: string;
  jobTypeId: string | null;
  jobTypeName: string | null;
  classConfidence: number | null;
  sequenceStatus?: string | null;
  stepEnteredAt: string;
  thumbnailPath: string | null;
  candidateForms: CandidateForm[];
  /** R-262 5d-5b — batch grouping fields */
  batchId: string;
  batchLabel: string;
  processingMode: string;
  /** CS-2 — current CS residency status; null = no hold. */
  csResidency: string | null;
}

/** R-262 5d-5b — a held (ready@classify) doc that is waiting for batch release. */
export interface WaitingDoc {
  id: string;
  displayId: string | null;
  filename: string;
  pageCount: number;
  formName: string | null;
}

/** R-264 5e-2 — one failed balance rule (persisted Batch.balanceFailures shape). */
export interface BalanceFailureDto {
  ruleId: string;
  ruleName: string;
  message: string;
  declared: unknown;
  actual: unknown;
  severity: "error" | "warning";
  overrideBehavior: string;
  trigger?: string;
  overridden?: boolean;
  declaredFieldRef?: string | null;
}

/** R-264 5e-2 — a balance_review batch surfaced in the classify queue. */
export interface BalanceParkedBatch {
  batchId: string;
  batchLabel: string;
  jobTypeName: string | null;
  processingMode: string;
  failures: BalanceFailureDto[];
  headerFieldValues: Record<string, string | number>;
}

/** R-262 5d-5b — full queue data shape returned by the API. */
export interface ClassifyQueueData {
  items: ClassifyItem[];
  /** Keyed by batchId; only batch-mode batches have entries. */
  waiting: Record<string, WaitingDoc[]>;
  /** R-264 5e-2 — batches parked in balance_review. */
  balanceParked: BalanceParkedBatch[];
  /** R-268 6f — per-batch count of parked-unidentified docs eligible for re-score. */
  reresolvable?: Record<string, number>;
  /** CS-2 §7 — true when the caller has delete-doc@classify permission; used to
   *  gate the Purge action on pending-deletion queue rows. Computed ONCE per
   *  request by the API — not per item. */
  canDeleteInClassify: boolean;
}

export interface ClassifyPage {
  id: string;
  pageNumber: number;
  thumbnailPath: string | null;
  pageTypeId: string | null;
  pageTypeName: string | null;
  pageLabel: string | null;
  pageClassSource: string | null;
  pageClassConfidence: number | null;
}

export interface ClassifySibling {
  id: string;
  displayId: string | null;
  filename: string;
  pageCount: number;
  formName: string | null;
}

/** R-262 5d-5b — scan-order neighbor (prev/next) for the classify panel. */
export interface ClassifyNeighbor {
  id: string;
  displayId: string | null;
  filename: string;
  pageCount: number;
  currentStep: string;
  status: string;
  formName: string | null;
  /** R-265 5d-5c — whether the neighbor's resolved form accepts attachments
   *  (gates the quiet-attach buttons; mirrors the attach-service 409 guard). */
  allowsAttachments: boolean;
}

export interface ClassifyDetail {
  id: string;
  displayId: string | null;
  filename: string;
  parkReason: string;
  classification: string | null;
  classConfidence: number | null;
  classificationLocked: boolean;
  containerId: string | null;
  completeness: string | null;
  sequenceStatus: string | null;
  jobTypeId: string | null;
  candidateForms: CandidateForm[];
  /** R-266 6a — the batch job type's cross-type disposition: "off" | "route" |
   *  "reject_batch". Drives whether the picker spans foreign types and whether
   *  the "Reject batch" action is offered. */
  crossTypeRouting: string;
  /** R-266 6a — batch was uploaded with route-by-content (content fingerprint
   *  classification spanning job types). */
  routeByContent: boolean;
  /** R-266 6a — the fingerprint's suggested target (pre-selected + highlighted
   *  in the picker). Null when there is no cross-type suggestion. */
  suggestion: { jobTypeId: string | null; formId: string | null } | null;
  /** R-267 6d — declared form disagrees with an in-job-type fingerprint match.
   *  Null when the declared form is consistent with content fingerprinting. */
  declaredFormSuggestion: {
    declaredFormId: string | null;
    declaredFormName: string | null;
    suggestedFormId: string;
    suggestedFormName: string | null;
    confidence: number | null;
  } | null;
  form: ClassifyForm | null;
  duplex: boolean;
  batchId: string;
  siblings: ClassifySibling[];
  pages: ClassifyPage[];
  /** R-262 5d-5b — nearest non-voided batch-mates in scan order. */
  neighbors: { prev: ClassifyNeighbor | null; next: ClassifyNeighbor | null };
  /** CS-2 — current CS residency status; null = no hold. */
  csResidency: string | null;
  /** CS-2 — free-text note recorded when the residency was set. */
  csResidencyNote: string | null;
  /** CS-2 — user display name that last set the residency. */
  csResidencySetBy: string | null;
  /** CS-2 — ISO timestamp when residency was last changed. */
  csResidencyAt: string | null;
  /** CS-2 — true when the API permits deletion from the classify queue
   *  (pending_deletion docs with the right permissions). */
  canDeleteInClassify: boolean;
}

/**
 * R-262 5d-5b — returns the full queue shape: items (exception docs) and
 * waiting (held batch-mode docs keyed by batchId). Previously unwrapped to
 * just .items — callers updated to consume ClassifyQueueData.
 */
export function getClassifyQueue(jobTypeId?: string): Promise<ClassifyQueueData> {
  const path = jobTypeId
    ? `/classify?jobTypeId=${encodeURIComponent(jobTypeId)}`
    : "/classify";
  return api.get<ClassifyQueueData>(path).then((r) => ({
    items: r.items,
    waiting: r.waiting ?? {},
    balanceParked: r.balanceParked ?? [],
    reresolvable: r.reresolvable,
    canDeleteInClassify: r.canDeleteInClassify ?? false,
  }));
}

/** R-264 5e-2 — edit a batch's declared header values; the API re-runs the
 *  balance gate (immediate unpark on pass). */
export function patchHeaderValues(
  batchId: string,
  values: Record<string, string | number>,
): Promise<unknown> {
  return api.patch(`/batches/${batchId}/header-values`, { values });
}

/** R-264 5e-2 — override one soft balance failure (the API 409s hard rules). */
export function overrideBalanceFailure(batchId: string, ruleId: string): Promise<unknown> {
  return api.post(`/batches/${batchId}/balance-failures/${ruleId}/override`, {});
}

/** R-264 5e-2 — manual clear of balance_review (409 while un-overridden hard
 *  failures stand). */
export function clearBalanceReview(batchId: string): Promise<unknown> {
  return api.post(`/batches/${batchId}/clear-balance-review`, {});
}

/** C4 — cross-type routing detail: populated exactly when the classify moved
 *  the document to another job type's batch. */
export interface ClassifyRoutedInfo {
  jobTypeId: string;
  jobTypeName: string | null;
  batchId: string;
  batchName: string | null;
  /** E-050 — displayId-led identity (customDisplayLabel ?? displayId ?? name). */
  batchLabel?: string | null;
}

export function classifyDocument(
  id: string,
  formId: string,
  declareNewLayout?: boolean,
): Promise<{ ok: boolean; classificationKey: string; routed: ClassifyRoutedInfo | null }> {
  return api.post(`/documents/${id}/classify`, {
    formId,
    ...(declareNewLayout ? { declareNewLayout: true } : {}),
  });
}

/** Task 10/11 — one form's layout variants + whether the caller may declare
 *  another one. Mirrors the admin DTO in
 *  apps/api/src/routes/job-types/forms/layout-variants.ts. */
export interface LayoutVariantDto {
  id: string;
  label: string;
  isBaseline: boolean;
  status: string;
  observationCount: number;
  divergentObservationCount: number;
  lastMatchedAt: string | null;
  createdAt: string;
  isStale: boolean;
}

export interface LayoutVariantsForForm {
  variants: LayoutVariantDto[];
  /** E-059 — `code` is the structural refusal discriminator (never parse `reason`). */
  canDeclare: {
    allowed: boolean;
    reason: string | null;
    code?: "not_found" | "not_enabled" | "zoned" | "at_cap" | null;
  };
}

/**
 * Task 11 — read-only lookup the station uses to decide whether "New layout
 * of this form" may be offered for the operator's currently-selected
 * candidate form. `jobTypeId` is that form's OWNING job type (candidate
 * forms can span job types under cross-type routing — see
 * `CandidateForm.jobTypeId` — so this is never assumed to be the batch's own).
 */
export function getLayoutVariantsForForm(
  jobTypeId: string,
  formId: string,
): Promise<LayoutVariantsForForm> {
  return api.get<LayoutVariantsForForm>(`/job-types/${jobTypeId}/forms/${formId}/layout-variants`);
}

/**
 * R-266 6a (Task 8/10) — `reject_batch` disposition: a confirmed cross-type
 * intruder poisons an exact-composition batch, so the API voids every live doc
 * and dissolves the batch. Mounted at POST /api/documents/batches/:id/reject-batch.
 */
export function rejectBatch(batchId: string, note?: string): Promise<void> {
  return api.post(`/documents/batches/${batchId}/reject-batch`, { note });
}

/**
 * R-268 6f — manually re-score parked-unidentified docs for a batch against
 * the matured fingerprint store. Mounted at POST
 * /api/documents/batches/:id/recheck-unidentified.
 */
export function recheckUnidentified(batchId: string): Promise<{ rechecked: number }> {
  return api.post(`/documents/batches/${batchId}/recheck-unidentified`);
}

export function getClassifyDetail(documentId: string): Promise<ClassifyDetail> {
  return api.get<ClassifyDetail>(`/classify/${documentId}`);
}

export function advancePartial(documentId: string): Promise<void> {
  return api.post(`/documents/${documentId}/advance-partial`, {});
}

export type PageAssignment =
  | { kind: "form_page"; pageTypeId: string }
  | { kind: "attachment" };

export interface AssignPageIdentityResult {
  ok: boolean;
  advanced: boolean;
  currentStep?: string;
}

export function assignPageIdentity(
  documentId: string,
  pageId: string,
  assignment: PageAssignment,
): Promise<AssignPageIdentityResult> {
  return api.post<AssignPageIdentityResult>(`/documents/${documentId}/pages/${pageId}/identity`, { assignment });
}

export interface ReorderPagesResult {
  ok: boolean;
  advanced: boolean;
  currentStep?: string;
}

export function reorderPages(
  documentId: string,
  orderedPageIds: string[],
): Promise<ReorderPagesResult> {
  return api.post<ReorderPagesResult>(`/documents/${documentId}/pages/reorder`, { orderedPageIds });
}

export interface SplitResult {
  ok: boolean;
  childCount: number;
  advancedCount: number;
}

export function splitDocument(documentId: string, boundaries: string[]): Promise<SplitResult> {
  return api.post<SplitResult>(`/documents/${documentId}/pages/split`, { boundaries });
}

export interface MoveMergeResult {
  ok: boolean;
  advanced: boolean;
}

export function movePage(documentId: string, pageId: string, targetDocumentId: string): Promise<MoveMergeResult> {
  return api.post<MoveMergeResult>(`/documents/${documentId}/pages/${pageId}/move`, { targetDocumentId });
}

export function mergeDocuments(documentId: string, sourceDocumentId: string): Promise<MoveMergeResult> {
  return api.post<MoveMergeResult>(`/documents/${documentId}/merge`, { sourceDocumentId });
}

/**
 * R-262 5d-5b — quietly attach one page of a parked exception doc to an
 * advanced batch-mate. Target step/status/lock are never touched.
 */
export function attachPage(
  documentId: string,
  pageId: string,
  targetDocumentId: string,
): Promise<MoveMergeResult> {
  return api.post<MoveMergeResult>(`/documents/${documentId}/pages/${pageId}/attach`, {
    targetDocumentId,
  });
}

/**
 * R-262 5d-5b — attach ALL pages of a parked exception doc to an advanced
 * batch-mate; origin is voided "merged". Use when operator wants to absorb the
 * whole stray doc without touching the target's step or completeness.
 */
export function attachAllPages(
  documentId: string,
  targetDocumentId: string,
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/documents/${documentId}/attach-all`, { targetDocumentId });
}

export interface DeletePageResult {
  ok: boolean;
  advanced: boolean;
  documentDeleted: boolean;
}

export function deletePage(
  documentId: string,
  pageId: string,
  rescan?: { note?: string },
): Promise<DeletePageResult> {
  return api.post<DeletePageResult>(`/documents/${documentId}/pages/${pageId}/delete`, rescan ? { rescan } : {});
}

export function deleteDocumentApi(documentId: string, rescan?: { note?: string }): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/documents/${documentId}/delete`, rescan ? { rescan } : {});
}

/**
 * CS-2 — set or clear a CS residency hold on a document.
 * action "research_hold" places the hold; "clear" lifts it.
 * note is only sent when provided (clear typically omits it).
 */
export function setCsResidency(
  documentId: string,
  action: "research_hold" | "clear",
  note?: string,
): Promise<{ ok: boolean }> {
  return api.post(`/documents/${documentId}/cs-residency`, note !== undefined ? { action, note } : { action });
}

/**
 * CS-2 — flag a document for deletion (pending_deletion state).
 * Immediate purge of a pending_deletion doc reuses the existing deleteDocumentApi.
 */
export function flagForDeletion(
  documentId: string,
  note?: string,
): Promise<{ ok: boolean; deleted: boolean }> {
  return api.post(`/documents/${documentId}/flag-deletion`, note !== undefined ? { note } : {});
}

export const PARK_REASON_GUIDANCE: Record<
  string,
  {
    label: string;
    guidance: string;
    liveAction: "form" | "advance" | null;
    editable?: boolean;
    // The park reason is resolved by a live affordance shown elsewhere in the
    // panel (e.g. the filmstrip reorder), so it shows no "coming" placeholder.
    resolvedLive?: boolean;
  }
> = {
  locked_redo: {
    label: "Wrong type",
    guidance: "Flagged as the wrong type — pick the correct form.",
    liveAction: "form",
  },
  no_form: {
    label: "No form",
    guidance: "No form matched — pick the document's form.",
    liveAction: "form",
  },
  split_orphan: {
    label: "Unidentified pages",
    guidance:
      "Unidentified pages from a split — assign the correct form, merge into a batch-mate, or delete.",
    liveAction: "form",
  },
  incomplete: {
    label: "Incomplete",
    guidance: "Missing or mis-identified pages — correct a page's type below, or accept as partial.",
    liveAction: "advance",
    editable: true,
  },
  misordered: {
    label: "Out of order",
    guidance: "Pages are out of the expected order — reorder them in the filmstrip and Apply, or correct a page's type below.",
    liveAction: null,
    editable: true,
    resolvedLive: true, // reorder shipped in 5d-3 (filmstrip Apply order)
  },
  low_confidence: {
    label: "Low confidence",
    guidance: "Low-confidence auto-classification — confirm or correct the form.",
    liveAction: "form",
  },
  unknown: {
    label: "Review",
    guidance: "Parked for review.",
    liveAction: null,
  },
};
